import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "src/layout/admin";
import GenericDataLayout from "src/components/custom/GenericDataLayout";
import GenericTable from "src/components/custom/GenericTable";
import Modal from "src/components/modal";
import Button from "src/components/custom/Button";
import CustomInput from "src/components/custom/CustomInput";
import { useForm } from "react-hook-form";
import {
  getMethodApiCall,
  postMethodApiCall,
  putMethodApiCall,
} from "src/services/api";
import { toast } from "react-toastify";

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  trade_name: "",
};

const MODAL_MODE = {
  ADD: "add",
  EDIT: "edit",
};

// ─── Component ────────────────────────────────────────────────────────────────

const Trade = () => {
  // ── Table state ────────────────────────────────────────────────────────────
  const [tableRows, setTableRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
  });

  // ── Modal state ────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(MODAL_MODE.ADD);
  const [selectedRow, setSelectedRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ── Form ───────────────────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: EMPTY_FORM });

  // ─── List fetch ─────────────────────────────────────────────────────────────

  const fetchList = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setListLoading(true);
      try {
        const res = await getMethodApiCall("/v1/api/mcc/trades", {
          page,
          limit,
        });
        if (res?.status === 200 && res?.data?.success) {
          setTableRows(res.data.data || []);
          const p = res.data.pagination || {};
          setPagination((prev) => ({
            ...prev,
            page: p.page ?? page,
            limit: p.limit ?? limit,
            totalRecords: p.total ?? 0,
          }));
        } else {
          setTableRows([]);
        }
      } catch {
        setTableRows([]);
        toast.error("Failed to load trades", 1000);
      } finally {
        setListLoading(false);
      }
    },
    [pagination.limit]
  );

  useEffect(() => {
    fetchList(1, 10);
  }, []);

  // ─── Modal open / close ─────────────────────────────────────────────────────

  const openModal = (mode, row = null) => {
    setModalMode(mode);
    setSelectedRow(row);

    if (mode === MODAL_MODE.ADD) {
      reset(EMPTY_FORM);
    } else if (row) {
      reset({
        trade_name: row.trade_name ?? "",
      });
    }

    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  // ─── Form submit ────────────────────────────────────────────────────────────

  const onSubmit = async (data) => {
    setSubmitting(true);

    const payload = {
      trade_name: data.trade_name.trim(),
    };

    console.log(selectedRow,  "selectedRow")
    try {
      let res;
      if (modalMode === MODAL_MODE.ADD) {
        res = await postMethodApiCall("/v1/api/mcc/trades", payload);
      } else {
        res = await putMethodApiCall(
          `/v1/api/mcc/trades/${selectedRow.id}`,
          payload
        );
      }

      if (res?.data?.success) {
        toast.success(res.data.message || "Operation successful", 1000);
        closeModal();
        fetchList();
      } else {
        toast.error(res?.data?.message || "Operation failed", 1000);
      }
    } catch {
      toast.error("An error occurred. Please try again.", 1000);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Table config ───────────────────────────────────────────────────────────

  const tableColumns = [
    { header: "TRADE NAME", accessor: "trade_name" },
    {
      header: "ACTIONS",
      accessor: "actions",
      render: (row) => (
        <button
          onClick={() => openModal(MODAL_MODE.EDIT, row)}
          className="text-xs px-3 py-1 rounded border border-primary text-primary hover:bg-primary/10 font-medium transition"
        >
          Edit
        </button>
      ),
    },
  ];

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <GenericDataLayout
        title="Trade"
        actions={[{ label: "Add New", onClick: () => openModal(MODAL_MODE.ADD) }]}
        isLoading={listLoading}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          totalRecords: pagination.totalRecords,
          onPageChange: (p) => fetchList(p, pagination.limit),
          onLimitChange: (l) => fetchList(1, l),
        }}
      >
        <GenericTable
          columns={tableColumns}
          data={tableRows}
          pagination={pagination}
        />
      </GenericDataLayout>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={modalMode === MODAL_MODE.ADD ? "Add Trade" : "Edit Trade"}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="space-y-5">
            <CustomInput
              label="Trade Name"
              name="trade_name"
              register={register}
              validation={{ required: "Trade name is required" }}
              error={errors.trade_name}
            />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button
              title={submitting ? "Processing..." : "Save"}
              disabled={submitting}
              type="submit"
            />
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
};

export default Trade;